import { useCallback, useSyncExternalStore } from 'react';
import { useIsFocused } from '@react-navigation/native';
import { useNavigation } from 'expo-router';

/**
 * A safer alternative to `useIsFocused` from `@react-navigation/native`
 * that works in both react-navigation and expo-router contexts.
 *
 * When rendered inside a `NavigationContainer`, behaves identically to
 * `useIsFocused` — returns a stateful boolean that updates on focus/blur.
 *
 * When rendered outside a `NavigationContainer` (e.g. `expo-router` screens),
 * subscribes to focus/blur events via `useSyncExternalStore` for a reactive
 * boolean that updates correctly on screen transitions.
 */
export const useSafeIsFocused = (): boolean => {
  try {
    return useIsFocused();
  } catch {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useExpoRouterIsFocused();
  }
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Uses `useSyncExternalStore` to subscribe to navigation focus/blur events,
 * reading `navigation.isFocused` as the snapshot. This ensures the value
 * stays in sync with navigation state and avoids tearing during concurrent renders.
 */
const useExpoRouterIsFocused = (): boolean => {
  const navigation = useNavigation();

  // ─────────────────────────────────────────────────────────────────────

  const subscribe = useCallback(
    (callback: () => void) => {
      const unsubscribeFocus = navigation.addListener('focus', callback);
      const unsubscribeBlur = navigation.addListener('blur', callback);

      return () => {
        unsubscribeFocus();
        unsubscribeBlur();
      };
    },
    [navigation],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useSyncExternalStore(subscribe, navigation.isFocused, navigation.isFocused);
};
